import React, { useState } from "react";
import styled from "@emotion/styled";
import type { Customer } from "../../types/customer";
import { useGetTemplatesQuery, useCreatePageMutation } from "../../api/pagesApi";
import type { CreateLandingPageRequest } from "../../api/pagesApi";

/* -------------------------------------------------------------------------- */
/*                                    TYPES                                   */
/* -------------------------------------------------------------------------- */

interface CustomerDetailProps {
  /** Whether the detail modal is visible */
  isOpen: boolean;
  /** Currently selected customer. If null, the modal is hidden. */
  customer: Customer | null;
  /** Called when the user closes the modal */
  onClose: () => void;
  /** Called when the user wants to edit the customer */
  onEdit?: (customer: Customer) => void;
}

/* -------------------------------------------------------------------------- */
/*                                   STYLES                                   */
/* -------------------------------------------------------------------------- */

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 1100;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EditButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #1a202c;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: #4a5568;
  padding: 4px 8px;
  transition: color 0.15s;

  &:hover {
    color: #1a202c;
  }
`;

const Body = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
`;

const InfoList = styled.dl`
  display: grid;
  grid-template-columns: minmax(120px, 200px) 1fr;
  row-gap: 8px;
  column-gap: 16px;
  font-size: 14px;
`;

const Term = styled.dt`
  color: #4a5568;
`;

const Desc = styled.dd`
  margin: 0;
  color: #2d3748;
  word-break: break-word;
`;

const Badge = styled.span<{ type: "plan" | "status"; value: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  ${({ type, value }) => {
    if (type === "plan") {
      switch (value) {
        case "pro":
          return "background:#bee3f8;color:#2a4365;";
        case "starter":
          return "background:#d6f5d6;color:#22543d;";
        default:
          return "background:#e2e8f0;color:#4a5568;";
      }
    }
    // status
    switch (value) {
      case "active":
        return "background:#c6f6d5;color:#22543d;";
      case "inactive":
        return "background:#fed7d7;color:#742a2a;";
      case "pending":
        return "background:#fef5e7;color:#744210;";
      case "canceled":
        return "background:#e2e8f0;color:#4a5568;";
      default:
        return "background:#e2e8f0;color:#4a5568;";
    }
  }}
`;

const CreatePageForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafafa;
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background: #f7fafc;
    color: #a0aec0;
  }
`;

const FormSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background: #f7fafc;
    color: #a0aec0;
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const CreateButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  background: transparent;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
  }
`;

const ToggleButton = styled.button`
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 12px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  font-size: 12px;
  margin-top: 4px;
`;

const SuccessMessage = styled.div`
  color: #38a169;
  font-size: 12px;
  margin-top: 4px;
`;

const PageActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button<{ variant?: 'preview' | 'open' }>`
  background: ${({ variant }) => 
    variant === 'preview' ? 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)' : 
    'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
  };
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* -------------------------------------------------------------------------- */
/*                                 COMPONENT                                  */
/* -------------------------------------------------------------------------- */

export const CustomerDetail: React.FC<CustomerDetailProps> = ({ isOpen, customer, onClose, onEdit }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState<CreateLandingPageRequest>({
    title: '',
    slug: '',
    template: 'modern',
    customerId: customer?.id || '',
    config: {
      heroTitle: '',
      heroSubtitle: '',
      ctaText: 'Get Started',
      theme: 'default',
      colors: {
        primary: '#3B82F6',
        secondary: '#1F2937',
        accent: '#10B981'
      }
    }
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const { data: templates, isLoading: templatesLoading } = useGetTemplatesQuery();
  const [createPage, { isLoading: isCreating }] = useCreatePageMutation();

  if (!customer) return null;

  const handleEdit = () => {
    if (onEdit) {
      onEdit(customer);
    }
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      template: 'modern',
      customerId: customer.id,
      config: {
        heroTitle: '',
        heroSubtitle: '',
        ctaText: 'Get Started',
        theme: 'default',
        colors: {
          primary: '#3B82F6',
          secondary: '#1F2937',
          accent: '#10B981'
        }
      }
    });
    setError('');
    setSuccess('');
  };

  const handleCreatePage = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.slug.trim()) {
      setError('Slug is required');
      return;
    }

    try {
      await createPage({
        ...formData,
        customerId: customer.id
      }).unwrap();
      setSuccess('Landing page created successfully!');
      resetForm();
      setTimeout(() => {
        setShowCreateForm(false);
        setSuccess('');
      }, 2000);
    } catch (err: any) {
      setError(err?.data?.error || err?.error || 'Failed to create landing page');
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    resetForm();
  };

  const getPageUrl = (slug: string) => {
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8080';
    return `${baseUrl}/${slug}`;
  };

  const handlePreviewPage = (slug: string) => {
    const url = getPageUrl(slug);
    window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
  };

  const handleOpenInNewTab = (slug: string) => {
    const url = getPageUrl(slug);
    window.open(url, '_blank');
  };

  return (
    <Overlay isOpen={isOpen} onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{customer.name}</Title>
          <ActionButtons>
            {onEdit && (
              <EditButton onClick={handleEdit}>
                Edit Customer
              </EditButton>
            )}
            <CloseButton aria-label="Close customer details" onClick={onClose}>
              &times;
            </CloseButton>
          </ActionButtons>
        </Header>

        <Body>
          {/* -------------------------------------------------------------- */}
          {/* BASIC INFO                                                   */}
          {/* -------------------------------------------------------------- */}
          <Section>
            <SectionTitle>Basic Information</SectionTitle>
            <InfoList>
              <Term>Customer ID</Term>
              <Desc>{customer.id}</Desc>

              <Term>Name</Term>
              <Desc>{customer.name}</Desc>

              <Term>Email</Term>
              <Desc>{customer.email}</Desc>

              <Term>Created At</Term>
              <Desc>{new Date(customer.createdAt).toLocaleString()}</Desc>
            </InfoList>
          </Section>

          {/* -------------------------------------------------------------- */}
          {/* COMPANY INFO                                                 */}
          {/* -------------------------------------------------------------- */}
          <Section>
            <SectionTitle>Company</SectionTitle>
            <InfoList>
              <Term>Company Name</Term>
              <Desc>{customer.company}</Desc>

              <Term>Domain</Term>
              <Desc>
                <code style={{ background: "#f7fafc", padding: "2px 6px", borderRadius: 4 }}>
                  {customer.domain}
                </code>
              </Desc>

              {customer.customDomain && (
                <>
                  <Term>Custom Domain</Term>
                  <Desc>{customer.customDomain}</Desc>
                </>
              )}

              {customer.billingEmail && (
                <>
                  <Term>Billing Email</Term>
                  <Desc>{customer.billingEmail}</Desc>
                </>
              )}

              {customer.contactPerson && (
                <>
                  <Term>Contact Person</Term>
                  <Desc>{customer.contactPerson}</Desc>
                </>
              )}
            </InfoList>
          </Section>

          {/* -------------------------------------------------------------- */}
          {/* SUBSCRIPTION                                                 */}
          {/* -------------------------------------------------------------- */}
          <Section>
            <SectionTitle>Subscription</SectionTitle>
            <InfoList>
              <Term>Plan</Term>
              <Desc>
                <Badge type="plan" value={customer.plan}>{customer.plan}</Badge>
              </Desc>

              <Term>Status</Term>
              <Desc>
                <Badge type="status" value={customer.subscriptionStatus}>{customer.subscriptionStatus}</Badge>
              </Desc>
            </InfoList>
          </Section>

          {/* -------------------------------------------------------------- */}
          {/* STATS                                                        */}
          {/* -------------------------------------------------------------- */}
          <Section>
            <SectionTitle>Usage Statistics</SectionTitle>
            <InfoList>
              <Term>Users</Term>
              <Desc>{customer.stats?.totalUsers ?? 0}</Desc>

              <Term>Pages</Term>
              <Desc>{customer.stats?.totalPages ?? 0}</Desc>

              <Term>Leads</Term>
              <Desc>{customer.stats?.totalLeads ?? 0}</Desc>
            </InfoList>
          </Section>

          {/* -------------------------------------------------------------- */}
          {/* LANDING PAGES                                                */}
          {/* -------------------------------------------------------------- */}
          <Section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <SectionTitle>Landing Pages ({customer.landingPages?.length || 0})</SectionTitle>
              {!showCreateForm && (
                <ToggleButton onClick={() => setShowCreateForm(true)}>
                  + Create Landing Page
                </ToggleButton>
              )}
            </div>

            {showCreateForm && (
              <CreatePageForm onSubmit={handleCreatePage}>
                <FormRow>
                  <FormLabel htmlFor="title">Page Title *</FormLabel>
                  <FormInput
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Enter page title"
                    disabled={isCreating}
                  />
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="slug">URL Slug *</FormLabel>
                  <FormInput
                    id="slug"
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                    placeholder="url-slug"
                    disabled={isCreating}
                  />
                  <div style={{ fontSize: '12px', color: '#718096' }}>
                    Preview: {(process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:8080').replace('http://', '').replace('https://', '')}/{formData.slug || 'page-slug'}
                  </div>
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="template">Template</FormLabel>
                  <FormSelect
                    id="template"
                    value={formData.template}
                    onChange={(e) => setFormData(prev => ({ ...prev, template: e.target.value }))}
                    disabled={isCreating || templatesLoading}
                  >
                    {templates && Object.entries(templates?.data).map(([key, template]) => (
                      <option key={key} value={key}>
                        {template.name} - {template.description}
                      </option>
                    ))}
                  </FormSelect>
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="heroTitle">Hero Title</FormLabel>
                  <FormInput
                    id="heroTitle"
                    type="text"
                    value={formData.config?.heroTitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, heroTitle: e.target.value }
                    }))}
                    placeholder="Main headline for your page"
                    disabled={isCreating}
                  />
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="heroSubtitle">Hero Subtitle</FormLabel>
                  <FormInput
                    id="heroSubtitle"
                    type="text"
                    value={formData.config?.heroSubtitle || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, heroSubtitle: e.target.value }
                    }))}
                    placeholder="Supporting text for your headline"
                    disabled={isCreating}
                  />
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="ctaText">Call-to-Action Text</FormLabel>
                  <FormInput
                    id="ctaText"
                    type="text"
                    value={formData.config?.ctaText || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      config: { ...prev.config, ctaText: e.target.value }
                    }))}
                    placeholder="Get Started"
                    disabled={isCreating}
                  />
                </FormRow>

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}

                <FormButtons>
                  <CancelButton type="button" onClick={handleCancelCreate} disabled={isCreating}>
                    Cancel
                  </CancelButton>
                  <CreateButton type="submit" disabled={isCreating}>
                    {isCreating ? 'Creating...' : 'Create Landing Page'}
                  </CreateButton>
                </FormButtons>
              </CreatePageForm>
            )}

            {customer.landingPages && customer.landingPages.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: showCreateForm ? '16px' : '0' }}>
                {customer.landingPages.map((page) => (
                  <div
                    key={page.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      backgroundColor: page.isActive ? '#f0fff4' : '#f7fafc',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                        {page.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        <code style={{ background: '#e2e8f0', padding: '2px 4px', borderRadius: '3px' }}>
                          /{page.slug}
                        </code>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
                          {page.leads}
                        </div>
                        <div style={{ fontSize: '11px', color: '#718096' }}>
                          leads
                        </div>
                      </div>
                      <PageActions>
                        <ActionButton 
                          variant="open" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenInNewTab(page.slug);
                          }}
                          title="Open page in new tab"
                        >
                          🔗 Open
                        </ActionButton>
                      </PageActions>
                      <Badge type="status" value={page.isActive ? 'active' : 'inactive'}>
                        {page.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(!customer.landingPages || customer.landingPages.length === 0) && !showCreateForm && (
              <div style={{ textAlign: 'center', color: '#718096', fontSize: '14px', padding: '20px' }}>
                No landing pages created yet.
              </div>
            )}
          </Section>

          {/* -------------------------------------------------------------- */}
          {/* USERS                                                        */}
          {/* -------------------------------------------------------------- */}
          {customer.users && customer.users.length > 0 && (
            <Section>
              <SectionTitle>Users ({customer.users.length})</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {customer.users.map((user) => (
                  <div
                    key={user.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      backgroundColor: '#f9f9f9',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>
                        {user.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#718096' }}>
                        {user.email}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Badge type="plan" value={user.role}>{user.role}</Badge>
                      {user.lastLoginAt && (
                        <div style={{ fontSize: '11px', color: '#718096' }}>
                          Last login: {new Date(user.lastLoginAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </Body>
      </Modal>
    </Overlay>
  );
};

export default CustomerDetail;
